import { Icons } from '@/components/icons';
import InfiniteScroll from '@/components/infinite-scroll';
import { getRedirectUrl } from '@/components/sidebar/components/floating-menu/components/notifications/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useGetWorkEngagement } from '@/config/accounts/users/organization/workengagement/client';
import { useMutateNotification } from '@/config/communication/notification/mutate';
import { cn } from '@/lib/cn';
import { toReadableDateTime } from '@/lib/dates';
import {
  BellIcon,
  BellSlashIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/16/solid';
import {
  ContentTypeToName,
  OrganizationUserWorkEngagementConfig,
  type ContentType,
  type Notification,
} from '@pigello/pigello-matrix';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { DateTime } from 'luxon';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { DashBoard, WorkEngagementDashboard } from '../types';
import { FacetedFilter } from './filter-checkbox';
import { useSession } from '@/providers/session-provider';
import { usePerms } from '@/hooks/usePerms';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      delayChildren: 0.2,
      staggerChildren: 0.07,
    },
  },
};

const nonClickableNotificationEvents = [
  'accounts.organizationuser.userhotploginlinkcreated',
];
export function DashboardNotifications({
  dashboards,
  dashboardKey,
}: {
  dashboards: (DashBoard | WorkEngagementDashboard)[] | undefined;
  dashboardKey: string | undefined;
}) {
  const {
    data: {
      session: { userId },
    },
  } = useSession();
  const { canView } = usePerms('notification');
  const { canView: canViewWorkEngagement } = usePerms('workengagement');
  const [selected, setSelected] = useState<string[]>();
  const dashboard = dashboards?.find((d) => d.key === dashboardKey);
  const workEngagementId =
    dashboard && 'work_engagement_id' in dashboard
      ? dashboard.work_engagement_id
      : undefined;

  const { data: workEngagement } = useGetWorkEngagement({
    id: workEngagementId,
    enabled: !!workEngagementId && !!dashboard?.key && canViewWorkEngagement,
  });

  const workEngagementNotifications: string[] = useMemo(
    () => workEngagement?.templateNotifications ?? [],
    [workEngagement?.templateNotifications]
  );

  const groupedNotifications = useMemo(() => {
    const notificationContentTypes: { v: string; d: string }[] =
      OrganizationUserWorkEngagementConfig.fields.receiveNotificationsFor
        .choices;
    const grouped = Object.groupBy(
      notificationContentTypes.filter((notification) => {
        if (workEngagementNotifications.length === 0) return true;
        return workEngagementNotifications.includes(notification.v);
      }),
      (notification) =>
        notification.v.split('.').slice(0, 2).join('.') ?? notification.v
    );
    return grouped;
  }, [workEngagementNotifications]);

  const allEventIdentifiers =
    OrganizationUserWorkEngagementConfig.fields.receiveNotificationsFor.choices.map(
      (el) => el.v as string
    );

  const selectedEventIdentifiers = allEventIdentifiers.filter((notification) =>
    selected?.some((s) => notification.startsWith(s))
  );

  const hasSetDefaultSelected = useRef(false);
  useEffect(() => {
    if (
      workEngagementNotifications.length === 0 ||
      hasSetDefaultSelected.current
    ) {
      return;
    }
    const defaultSelected = workEngagementNotifications.map((notification) =>
      notification.split('.').slice(0, 2).join('.')
    );
    const uniqueDefaultSelected = [...new Set(defaultSelected)];
    setSelected(uniqueDefaultSelected);
    hasSetDefaultSelected.current = true;
  }, [workEngagementNotifications]);

  return (
    <Card className='gap-0 p-0'>
      <CardHeader className='grid gap-2 border-b-0 p-0 px-4 pt-4'>
        <div className='flex items-center justify-between'>
          <CardTitle>Notiser</CardTitle>
          {canView && (
            <FacetedFilter
              selected={selected}
              setSelected={setSelected}
              options={Object.keys(groupedNotifications)?.map(
                (notification) => ({
                  label: ContentTypeToName[notification as ContentType],
                  value: notification,
                })
              )}
              disabled={!dashboardKey}
            />
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!canView && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className='flex h-80 flex-col items-center justify-center gap-0.5 text-center'
          >
            <BellSlashIcon className='size-6' />
            <span className='text-sm font-medium'>Ingen åtkomst</span>
            <span className='text-xs'>Du har inte tillgång till notiser.</span>
          </motion.div>
        )}
        {canView && (
          <InfiniteScroll<Notification>
            modelName='notification'
            queryParams={{
              filters: {
                sent_to_organization_user: { noop: userId },
                read: { noop: false },
                created_at: {
                  __gte: DateTime.now().minus({ weeks: 2 }).toISODate(),
                },
                ...(selectedEventIdentifiers.length > 0 && {
                  event_identifier: { __in: selectedEventIdentifiers },
                }),
              },
            }}
          >
            {({
              data,
              rootRef,
              triggerLoadRef,
              hasNextPage,
              dataLoading,
              isInitialLoading,
            }) => (
              <div
                className='relative flex h-80 flex-col overflow-y-auto py-2'
                ref={rootRef}
              >
                <motion.div
                  variants={container}
                  initial='hidden'
                  animate='show'
                >
                  {data?.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                    />
                  ))}
                </motion.div>
                {!data?.length && !dataLoading && !isInitialLoading && (
                  <div className='flex h-full flex-col items-center justify-center gap-0.5 text-center'>
                    <BellIcon className='size-6' />
                    <span className='text-sm font-medium'>Inga notiser</span>
                    <span className='px-4 text-xs text-muted-foreground'>
                      {!dashboardKey
                        ? 'Välj eller skapa en dashboard för att se notiser'
                        : 'Inga notiser hittades.'}
                    </span>
                  </div>
                )}

                {isInitialLoading && (
                  <div className='flex size-full items-center justify-center'>
                    <div className='my-4 flex h-24 flex-col items-center justify-center gap-2'>
                      <Icons.loader className='size-4 animate-spin' />
                      <span className='text-xs font-medium text-muted-foreground'>
                        Hämtar notiser...
                      </span>
                    </div>
                  </div>
                )}
                {hasNextPage && (
                  <div
                    className='flex w-full items-center justify-center'
                    ref={triggerLoadRef}
                  >
                    <Icons.loader className='size-4 animate-spin' />
                  </div>
                )}
              </div>
            )}
          </InfiniteScroll>
        )}
      </CardContent>
    </Card>
  );
}

const item = {
  hidden: { opacity: 0, y: -5 },
  show: { opacity: 1, y: 0 },
};

function NotificationItem({ notification }: { notification: Notification }) {
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const res = await getRedirectUrl(notification.id);
      return { redirectUrl: res.redirectUrl };
    },
    onSuccess: ({ redirectUrl }) => {
      markAsRead({
        id: notification.id,
        body: { read: true },
      });
      if (redirectUrl) {
        router.push(redirectUrl);
      }
    },
    onError: () => {
      toast.error('Misslyckades med att hämta aviseringen');
    },
  });
  const { mutate: markAsRead, isPending: isMarkingAsRead } =
    useMutateNotification({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['notifications-all'],
        });
      },
      onError: () => {
        toast.error('Misslyckades med att markera som läst');
      },
    });
  const hasNonClickableNotification =
    !!notification.eventIdentifier &&
    nonClickableNotificationEvents.includes(notification.eventIdentifier);
  return (
    <motion.div
      variants={item}
      initial='hidden'
      animate='show'
      tabIndex={0}
      key={notification.id}
      role='button'
      onKeyDown={(e) => {
        if (
          e.key === 'Enter' &&
          hasNonClickableNotification &&
          (!isPending || !isMarkingAsRead)
        ) {
          markAsRead({
            id: notification.id,
            body: { read: true },
          });
          return;
        }
        if (
          e.key === 'Enter' &&
          !hasNonClickableNotification &&
          (!isPending || !isMarkingAsRead)
        ) {
          mutate();
        }
      }}
      onClick={() => {
        if (isPending || isMarkingAsRead) return;
        if (hasNonClickableNotification) {
          markAsRead({
            id: notification.id,
            body: { read: true },
          });
          return;
        }
        mutate();
      }}
      className='px-2'
    >
      <div
        className={cn(
          'group flex items-center gap-2 rounded-md p-2 text-left transition-colors hover:bg-accent',
          notification.read && 'opacity-70'
        )}
      >
        <div className='flex w-full items-center justify-between gap-2'>
          <div className='flex flex-1 flex-col gap-0.5'>
            <p className='text-xs font-medium break-all'>
              {notification.title}
            </p>
            <p
              className='text-xs'
              style={{
                wordBreak: 'break-word',
              }}
            >
              {notification.content.length > 50 && !expanded
                ? `${notification.content.substring(0, 50)}...`
                : notification.content}{' '}
              {notification.content.length > 50 && (
                <Button
                  size='sm'
                  variant='link'
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((prev) => !prev);
                  }}
                  className='h-auto p-0 text-xs whitespace-nowrap'
                >
                  {expanded ? ' Visa mindre' : ' Visa mer'}
                </Button>
              )}
            </p>
            <p className='w-full text-xs text-muted-foreground'>
              {toReadableDateTime(notification.createdAt)}
            </p>
          </div>

          <div className='flex items-center gap-2'>
            <DropdownMenu open={open} onOpenChange={setOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  className='invisible rounded-full group-hover:visible focus-within:visible data-[state=open]:visible'
                  size='icon'
                  variant='ghost'
                >
                  <EllipsisHorizontalIcon className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onClick={(e) => {
                    markAsRead({
                      id: notification.id,
                      body: { read: !notification.read },
                    });
                    setOpen(false);
                    e.stopPropagation();
                  }}
                >
                  Markera som {notification.read ? 'oläst' : 'läst'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {!notification.read && (
              <div className='size-2.5 rounded-full bg-primary' />
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
